/**
 * 客户端专用 Axios 请求封装
 *
 * 与业务端 request.js 完全隔离
 * 只读 CUSTOMER_PORTAL_TOKEN，不读 ShareWin-Token
 *
 * @author system
 * @date 2026-09-11
 */

import axios from 'axios'
import { Message } from 'element-ui'
import router from '@/router'

const WHITE_LIST = [
    '/api/captcha',
    '/customer/login',
    '/customer/logout'
]

const service = axios.create({
    baseURL: process.env.VUE_APP_BASE_API || '/api',
    timeout: 60000
})

let isLoggingOut = false

// ========== 请求拦截器 ==========
service.interceptors.request.use(
    config => {
        // 白名单接口：不带 token
        const isWhiteList = WHITE_LIST.some(url => config.url.includes(url))
        if (isWhiteList) {
            delete config.headers['Authorization']
            if (config.url.includes('/api/captcha')) {
                config.params = {
                    ...config.params,
                    _t: Date.now()
                }
            }
            return config
        }

        // ===== 只取客户端 token =====
        const token = localStorage.getItem('CUSTOMER_PORTAL_TOKEN')
        if (token) {
            config.headers['Authorization'] = 'Bearer ' + token
        }

        // GET 请求添加时间戳防止缓存
        if (config.method?.toLowerCase() === 'get') {
            config.params = {
                ...config.params,
                _t: Date.now()
            }
        }

        return config
    },
    error => Promise.reject(error)
)

// ========== 响应拦截器 ==========
service.interceptors.response.use(
    response => {
        if (response.request.responseType === 'blob') {
            return response
        }

        const res = response.data

        if (response.config.silent) {
            return res
        }

        if (res.code !== 200) {
            if (res.code === 401) {
                if (isLoggingOut) {
                    return Promise.reject(new Error('正在退出登录...'))
                }

                isLoggingOut = true

                localStorage.removeItem('CUSTOMER_PORTAL_TOKEN')
                localStorage.removeItem('CUSTOMER_PORTAL_ACCOUNT')
                localStorage.removeItem('CUSTOMER_PORTAL_NEED_CHANGE_PWD')

                setTimeout(() => {
                    isLoggingOut = false
                    router.push('/c/login')
                    Message({ message: '登录已过期，请重新登录', type: 'warning', duration: 3000 })
                }, 100)

                return Promise.reject(new Error('登录已过期，请重新登录'))
            }

            return Promise.reject(new Error(res.msg || '请求失败'))
        }
        return res
    },
    error => {
        if (error.response?.status === 401) {
            if (isLoggingOut) {
                return Promise.reject(error)
            }

            isLoggingOut = true

            localStorage.removeItem('CUSTOMER_PORTAL_TOKEN')
            localStorage.removeItem('CUSTOMER_PORTAL_ACCOUNT')
            localStorage.removeItem('CUSTOMER_PORTAL_NEED_CHANGE_PWD')

            setTimeout(() => {
                isLoggingOut = false
                router.push('/c/login')
            }, 100)

            return Promise.reject(error)
        }

        Message({ message: error.message || '网络错误', type: 'error', duration: 3000 })
        return Promise.reject(error)
    }
)

export default service
